/**
 * Фигура человека для схемы нагрузки — спереди и сзади.
 *
 * Нарисована с нуля из простых кривых, не обведена с чужих изображений:
 * картинки из атласов и иконочных наборов — чужой контент. Анатомия
 * упрощена ровно до групп, которые знает движок (`MuscleGroup`): спина —
 * одна область, плечи — одна группа спереди и сзади. Точности сверх этих
 * одиннадцати групп рисунок не обещает.
 *
 * Каждая фигура — в своей системе координат 200×390, центр по x = 100.
 * Левая и правая половины заданы отдельными контурами: так их проще выверять
 * глазами, чем через зеркальное преобразование.
 */

export type BodyView = 'front' | 'back'

export interface Region {
  view: BodyView
  group: string
  d: string
}

export const FIGURE_WIDTH = 200
export const FIGURE_HEIGHT = 390

function circle(cx: number, cy: number, r: number): string {
  return `M${cx - r},${cy} a${r},${r} 0 1,0 ${2 * r},0 a${r},${r} 0 1,0 ${-2 * r},0 Z`
}

function ellipse(cx: number, cy: number, rx: number, ry: number): string {
  return `M${cx - rx},${cy} a${rx},${ry} 0 1,0 ${2 * rx},0 a${rx},${ry} 0 1,0 ${-2 * rx},0 Z`
}

const HEAD = circle(100, 26, 17)
const NECK = 'M96,41 h8 q4,0 4,4 v6 q0,4 -4,4 h-8 q-4,0 -4,-4 v-6 q0,-4 4,-4 Z'
const TORSO = 'M60,58 Q100,48 140,58 L148,72 L142,150 Q140,176 134,192 L66,192 Q60,176 58,150 L52,72 Z'
const HANDS = [ellipse(38, 190, 7, 9), ellipse(162, 190, 7, 9)]
const FEET = [ellipse(79, 378, 10, 5), ellipse(121, 378, 10, 5)]

/** Части тела, которые не относятся ни к одной группе: голова, кисти, колени, стопы. */
export const SILHOUETTE: Record<BodyView, string[]> = {
  front: [
    HEAD,
    NECK,
    TORSO,
    'M66,190 L134,190 L138,212 L62,212 Z',
    ...HANDS,
    ellipse(78, 296, 11, 8),
    ellipse(122, 296, 11, 8),
    'M68,304 Q64,336 72,372 L86,372 Q92,336 88,304 Z',
    'M132,304 Q136,336 128,372 L114,372 Q108,336 112,304 Z',
    ...FEET,
  ],
  back: [
    HEAD,
    NECK,
    TORSO,
    'M84,152 L116,152 L122,190 L78,190 Z',
    ...HANDS,
    ellipse(78, 298, 10, 6),
    ellipse(122, 298, 10, 6),
    'M72,358 L86,358 L86,372 L72,372 Z',
    'M114,358 L128,358 L128,372 L114,372 Z',
    ...FEET,
  ],
}

/** Тонкие линии поверх областей: кубики пресса, позвоночник и лопатки. */
export const DETAIL_LINES: Record<BodyView, string> = {
  front: 'M100,104 L100,176 M82,124 L118,124 M82,142 L118,142 M83,158 L117,158',
  back: 'M100,56 L100,150 M78,70 Q100,96 122,70',
}

/** Области групп. Каждая запись — на своей строке: так их сверяет тест. */
export const BODY_REGIONS: Region[] = [
  { view: 'front', group: 'shoulders', d: 'M56,60 Q44,62 38,80 Q41,92 52,94 Q60,82 64,64 Z' },
  { view: 'front', group: 'shoulders', d: 'M144,60 Q156,62 162,80 Q159,92 148,94 Q140,82 136,64 Z' },
  { view: 'front', group: 'chest', d: 'M66,66 Q83,58 98,64 L98,96 Q84,106 68,99 Q61,84 66,66 Z' },
  { view: 'front', group: 'chest', d: 'M134,66 Q117,58 102,64 L102,96 Q116,106 132,99 Q139,84 134,66 Z' },
  { view: 'front', group: 'abs', d: 'M81,106 Q100,101 119,106 L117,172 Q100,180 83,172 Z' },
  { view: 'front', group: 'biceps', d: 'M38,94 Q33,112 36,130 Q44,135 51,128 Q56,110 53,96 Z' },
  { view: 'front', group: 'biceps', d: 'M162,94 Q167,112 164,130 Q156,135 149,128 Q144,110 147,96 Z' },
  { view: 'front', group: 'forearms', d: 'M36,134 Q30,154 32,178 Q39,183 45,178 Q52,156 51,132 Z' },
  { view: 'front', group: 'forearms', d: 'M164,134 Q170,154 168,178 Q161,183 155,178 Q148,156 149,132 Z' },
  { view: 'front', group: 'quads', d: 'M62,210 Q57,246 64,284 Q76,294 90,286 Q99,248 99,212 Z' },
  { view: 'front', group: 'quads', d: 'M138,210 Q143,246 136,284 Q124,294 110,286 Q101,248 101,212 Z' },
  { view: 'back', group: 'back', d: 'M66,60 Q100,50 134,60 L142,78 Q134,122 118,152 L82,152 Q66,122 58,78 Z' },
  { view: 'back', group: 'shoulders', d: 'M56,60 Q44,62 38,80 Q41,92 52,94 Q60,82 64,64 Z' },
  { view: 'back', group: 'shoulders', d: 'M144,60 Q156,62 162,80 Q159,92 148,94 Q140,82 136,64 Z' },
  { view: 'back', group: 'triceps', d: 'M38,94 Q33,112 36,130 Q44,135 51,128 Q56,110 53,96 Z' },
  { view: 'back', group: 'triceps', d: 'M162,94 Q167,112 164,130 Q156,135 149,128 Q144,110 147,96 Z' },
  { view: 'back', group: 'forearms', d: 'M36,134 Q30,154 32,178 Q39,183 45,178 Q52,156 51,132 Z' },
  { view: 'back', group: 'forearms', d: 'M164,134 Q170,154 168,178 Q161,183 155,178 Q148,156 149,132 Z' },
  { view: 'back', group: 'glutes', d: 'M64,192 Q60,218 74,230 Q92,236 99,220 L99,192 Z' },
  { view: 'back', group: 'glutes', d: 'M136,192 Q140,218 126,230 Q108,236 101,220 L101,192 Z' },
  { view: 'back', group: 'hamstrings', d: 'M64,234 Q59,262 66,290 Q78,298 90,290 Q99,262 99,238 Z' },
  { view: 'back', group: 'hamstrings', d: 'M136,234 Q141,262 134,290 Q122,298 110,290 Q101,262 101,238 Z' },
  { view: 'back', group: 'calves', d: 'M66,304 Q59,330 69,356 Q80,362 88,352 Q93,326 87,302 Z' },
  { view: 'back', group: 'calves', d: 'M134,304 Q141,330 131,356 Q120,362 112,352 Q107,326 113,302 Z' },
]
